import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, LoaderCircle } from "lucide-react";
import { queryKeys } from "@/constants/queryKeys";
import { initialsFromName } from "@/lib/media";
import { updateProfile, updateProfilePic, validateProfilePicFile } from "@/services/api";
import { ApiError } from "@/services/http/errors";
import { useAuthStore } from "@/store";
import type { AuthProfile } from "@/types/api";
import { Button } from "@/components/ui";
import {
  SettingsCard,
  SettingsField,
  SettingsNote,
  SettingsPanel,
  SettingsRow,
} from "../SettingsUI";

export function ProfileSection({ profile }: { profile: AuthProfile | null }) {
  const setProfile = useAuthStore((s) => s.setProfile);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(profile?.name ?? "");
  const [phone, setPhone] = useState(profile?.ph_no ?? "");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(profile?.name ?? "");
    setPhone(profile?.ph_no ?? "");
    setAvatarFailed(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [profile?.id, profile?.name, profile?.ph_no, profile?.profile_pic]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const mutation = useMutation({
    mutationFn: () =>
      updateProfile({
        name: displayName.trim(),
        ph_no: phone.trim(),
      }),
    onSuccess: (data) => {
      setProfile(data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      setError(null);
      setMessage("Profile updated successfully.");
    },
    onError: (err) => {
      setMessage(null);
      setError(err instanceof ApiError ? err.userMessage : "Could not update profile.");
    },
  });

  const picMutation = useMutation({
    mutationFn: (file: File) => updateProfilePic(file),
    onSuccess: (data) => {
      setProfile(data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setAvatarFailed(false);
      setError(null);
      setMessage("Profile picture updated.");
    },
    onError: (err) => {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setMessage(null);
      setError(err instanceof ApiError ? err.userMessage : "Could not update profile picture.");
    },
  });

  function onPickPhoto() {
    if (picMutation.isPending) return;
    fileInputRef.current?.click();
  }

  function onPhotoSelected(file: File | undefined) {
    if (!file) return;
    const validationError = validateProfilePicFile(file);
    if (validationError) {
      setMessage(null);
      setError(validationError);
      return;
    }
    setError(null);
    setMessage(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setAvatarFailed(false);
    picMutation.mutate(file);
  }

  if (!profile) {
    return (
      <SettingsPanel title="Profile" description="Name, profile picture, username">
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-fg-muted">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading profile…
        </div>
      </SettingsPanel>
    );
  }

  const avatar = previewUrl || profile.profile_pic;
  const initials = initialsFromName(profile.name || profile.username || "P");
  const showImage = Boolean(avatar) && !avatarFailed;
  const dirty =
    displayName.trim() !== (profile.name ?? "") ||
    phone.trim() !== (profile.ph_no ?? "");

  return (
    <SettingsPanel title="Profile" description="Name, profile picture, username">
      <div className="flex flex-col items-center rounded-[6px] border border-border bg-surface px-6 py-8">
        <div className="relative mb-4">
          <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full bg-papaya/15 text-2xl font-semibold text-papaya-deep">
            {showImage ? (
              <img
                src={avatar!}
                alt={profile.name}
                className="h-full w-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              initials
            )}
          </div>
          <button
            type="button"
            onClick={onPickPhoto}
            disabled={picMutation.isPending}
            className="absolute right-1 bottom-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-papaya-deep text-white shadow-sm hover:bg-papaya-deep/90 disabled:opacity-60"
            aria-label="Change profile picture"
          >
            {picMutation.isPending ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            className="hidden"
            onChange={(event) => {
              onPhotoSelected(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </div>
        <p className="text-xl font-semibold text-fg">{displayName || profile.name}</p>
        <p className="mt-1 text-sm text-fg-muted">{profile.email}</p>
        <p className="mt-1 text-xs text-fg-subtle">@{profile.username}</p>
        {profile.current_status ? (
          <p className="mt-2 rounded-full bg-papaya-leaf/10 px-2.5 py-1 text-xs font-medium text-papaya-leaf">
            {profile.current_status}
          </p>
        ) : null}
      </div>

      <SettingsCard title="Your details">
        <SettingsField
          label="Name"
          value={displayName}
          onChange={setDisplayName}
          hint="This name is visible to people and groups you message."
        />
        <SettingsField
          label="Username"
          value={profile.username}
          readOnly
          hint="People can find you with this username. It can’t be changed."
        />
        <SettingsField
          label="Phone"
          value={phone}
          onChange={setPhone}
          placeholder="Phone number"
        />
        <SettingsField
          label="Email"
          value={profile.email}
          readOnly
          hint="Managed by your organization."
        />
      </SettingsCard>

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-sm">
          {error ? <p className="text-danger">{error}</p> : null}
          {message ? <p className="text-papaya-leaf">{message}</p> : null}
        </div>
        <Button
          type="button"
          disabled={!dirty || mutation.isPending}
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Save changes
        </Button>
      </div>

      <SettingsCard title="Profile actions">
        <SettingsRow
          title="Organization"
          description={profile.organization?.name || "No organization"}
        />
        <SettingsRow
          title="Copy profile link"
          description={`papaya.local/u/${profile.username}`}
        />
      </SettingsCard>

      <SettingsNote>
        Name and phone save to your Papaya account. Username is assigned and locked. Profile
        photos must be JPEG, PNG, or GIF under 2 MB.
      </SettingsNote>
    </SettingsPanel>
  );
}
